import { useContext } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ArcadeBorderedCard, ArcadeButton, Button, Flex, Header, spacing, Stack, Text } from '@cash-design-system/react'
import { FormBlockerElementCallToActionElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { NestedCard } from '@cash-web/shared-feature-common'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

const Wrapper = styled(NestedCard)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.l};
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xs}`};
`

const MaxWidthImage = styled.img`
  max-width: 100%;
`

const ActionHeader = styled(Header)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
`

const ActionSubtitle = styled(Header)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
  color: ${props => props.theme.colors.tertiaryLabel};
`

export const ActionButton = styled(Button)`
  margin: ${({ theme: { spacing } }) => `${spacing.l} ${spacing.xxs} ${spacing.xs} ${spacing.xxs}`};
`

const ArcadeWrapper = styled(ArcadeBorderedCard)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.l};
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xs}`};
`

const ArcadeHeader = styled(Text)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
`

const ArcadeSubtitle = styled(Text)`
  margin: ${({ theme: { spacing } }) => `${spacing.empty} ${spacing.xxs}`};
`

export const ArcadeActionButton = styled(ArcadeButton)`
  margin: ${({ theme: { spacing } }) => `${spacing.l} ${spacing.xxs} ${spacing.xs} ${spacing.xxs}`};
`

// NOTE: The `accent_color` property is purposefully ignored: https://cash.slack.com/archives/C01M5JHQBCM/p1712264532738299
export const CallToActionElement = (props: FormBlockerElementCallToActionElement) => {
  const { setError } = useContext(ErrorContext)
  const { t } = useTranslation()
  const { darkMode } = useTheme()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const flow = useFlowContext()

  const textTokenizer = useTextTokenizer({ textLink: { target: '_blank' } })

  const handleClick = () => {
    if (props?.button_action) {
      flow.executeBlockerAction(props?.button_action)
    } else {
      setError(t('This action is not currently supported', { context: 'Error text.' }))
      logger.error(new Error('Missing blocker action in CallToAction element'))
    }
  }

  // NOTE: The image_width and image_height properties are ignored. They are also ignored in iOS.
  const imageUrl = darkMode ? props.image?.dark_url : props.image?.light_url

  const tokenizedTitle = textTokenizer(props.title)
  const tokenizedSubtitle = textTokenizer(props.subtitle)

  if (isArcadeMigrationEnabled) {
    return (
      <ArcadeWrapper>
        <Flex flexDirection="column" gap={spacing.l}>
          {imageUrl && (
            <Flex justifyContent="center" alignItems="center">
              <MaxWidthImage src={imageUrl} alt="" />
            </Flex>
          )}
          <Flex flexDirection="column" gap={spacing.s}>
            {props.title && (
              <ArcadeHeader data-testid="calltoaction-title" as="h1" typography="header" color="textStandard">
                {tokenizedTitle}
              </ArcadeHeader>
            )}
            {props.subtitle && (
              <ArcadeSubtitle data-testid="calltoaction-subtitle" color="textStandard" typography="body">
                {tokenizedSubtitle}
              </ArcadeSubtitle>
            )}
            {props.button_action && (
              <ArcadeActionButton
                data-testid="calltoaction-arcadebutton"
                size="cta"
                prominence="standard"
                onClick={handleClick}
              >
                {props?.button_action?.text}
              </ArcadeActionButton>
            )}
          </Flex>
        </Flex>
      </ArcadeWrapper>
    )
  }
  return (
    <Wrapper>
      <Stack space={spacing.l}>
        {imageUrl && (
          <Flex justifyContent="center" alignItems="center">
            <MaxWidthImage src={imageUrl} alt="" />
          </Flex>
        )}
        <Stack space={spacing.s}>
          {props.title && (
            <ActionHeader data-testid="calltoaction-title" variant="titleBarPageTitle">
              {tokenizedTitle}
            </ActionHeader>
          )}
          {props.subtitle && (
            <ActionSubtitle data-testid="calltoaction-subtitle" variant="body">
              {tokenizedSubtitle}
            </ActionSubtitle>
          )}
          {props.button_action && (
            <ActionButton
              data-testid="calltoaction-button"
              variant="secondary"
              size="medium"
              loading={false}
              onClick={handleClick}
            >
              {props?.button_action?.text}
            </ActionButton>
          )}
        </Stack>
      </Stack>
    </Wrapper>
  )
}

export default CallToActionElement
